import {
  nextTick,
  defineComponent,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
  ref,
} from 'vue';
import TransitionEvents from './css-animation/Event';
import raf from './raf';
import { getPrefixCls } from '../_utils/global-config';

const findDOMNode = (instance: any) => {
  let node = instance?.vnode?.el || (instance && (instance.$el || instance));
  while (node && !node.tagName) {
    node = node.nextSibling;
  }
  return node;
};

let styleForPesudo: HTMLStyleElement;

// Where el is the DOM element you'd like to test for visibility
function isHidden(element: HTMLElement) {
  if (process.env.NODE_ENV === 'test') {
    return false;
  }
  return !element || element.offsetParent === null;
}
function isNotGrey(color: string) {
  // eslint-disable-next-line no-useless-escape
  const match = (color || '').match(/rgba?\((\d*), (\d*), (\d*)(, [\.\d]*)?\)/);
  if (match && match[1] && match[2] && match[3]) {
    return !(match[1] === match[2] && match[2] === match[3]);
  }
  return true;
}
export default defineComponent({
  compatConfig: { MODE: 3 },
  name: 'Wave',
  props: {
    insertExtraNode: Boolean,
    disabled: Boolean,
  },
  setup(props, { slots, expose }) {
    const instance = getCurrentInstance();
    const prefixCls = ref(getPrefixCls());
    let eventIns: any = null;
    let clickWaveTimeoutId: any = null;
    let animationStartId: any = null;
    let animationStart = false;
    let extraNode: any = null;
    let isUnmounted = false;
    const onTransitionStart = (e) => {
      if (isUnmounted) return;

      const node = findDOMNode(instance);
      if (!e || e.target !== node) {
        return;
      }

      if (!animationStart) {
        resetEffect(node);
      }
    };
    const onTransitionEnd = (e: any) => {
      if (!e || e.animationName !== 'fadeEffect') {
        return;
      }
      resetEffect(e.target);
    };
    const getAttributeName = () => {
      const { insertExtraNode } = props;
      return insertExtraNode
        ? `${prefixCls.value}-click-animating`
        : `${prefixCls.value}-click-animating-without-extra-node`;
    };
    const onClick = (node: HTMLElement, waveColor: string) => {
      const { insertExtraNode, disabled } = props;
      if (
        disabled ||
        !node ||
        isHidden(node) ||
        node.className.indexOf('-leave') >= 0 ||
        node.className.indexOf('switch-type-line') >= 0
      ) {
        return;
      }
      extraNode = document.createElement('div');
      extraNode.className = `${prefixCls.value}-click-animating-node`;
      const attributeName = getAttributeName();

      node.removeAttribute(attributeName);
      node.setAttribute(attributeName, 'true');
      // Not white or transparent or grey
      styleForPesudo = styleForPesudo || document.createElement('style');
      if (
        waveColor &&
        waveColor !== '#ffffff' &&
        waveColor !== 'rgb(255, 255, 255)' &&
        isNotGrey(waveColor) &&
        !/rgba\(\d*, \d*, \d*, 0\)/.test(waveColor) && // any transparent rgba color
        waveColor !== 'transparent'
      ) {
        extraNode.style.borderColor = waveColor;
        styleForPesudo.innerHTML = `
        [${prefixCls.value}-click-animating-without-extra-node='true']::after, .${prefixCls.value}-click-animating-node {
          --antd-wave-shadow-color: ${waveColor};
        }`;
        if (!document.body.contains(styleForPesudo)) {
          document.body.appendChild(styleForPesudo);
        }
      }
      if (insertExtraNode) {
        node.appendChild(extraNode);
      }
      TransitionEvents.addStartEventListener(node, onTransitionStart);
      TransitionEvents.addEndEventListener(node, onTransitionEnd);
    };
    const resetEffect = (node: HTMLElement) => {
      if (!node || node === extraNode || !(node instanceof Element)) {
        return;
      }
      const { insertExtraNode } = props;
      const attributeName = getAttributeName();
      node.setAttribute(attributeName, 'false'); // edge has bug on `removeAttribute` #14466
      if (styleForPesudo) {
        styleForPesudo.innerHTML = '';
      }
      if (insertExtraNode && extraNode && node.contains(extraNode)) {
        node.removeChild(extraNode);
      }
      TransitionEvents.removeStartEventListener(node, onTransitionStart);
      TransitionEvents.removeEndEventListener(node, onTransitionEnd);
    };
    const bindAnimationEvent = (node: HTMLElement): any => {
      if (
        !node ||
        !node.getAttribute ||
        node.getAttribute('disabled') ||
        node.className.indexOf('disabled') >= 0
      ) {
        return;
      }
      const newClick = (e: MouseEvent) => {
        // Fix radio button click twice
        if (
          (e.target as any).tagName === 'INPUT' ||
          isHidden(e.target as HTMLElement)
        ) {
          return;
        }
        resetEffect(node);
        // Get wave color from target
        let waveColor =
          getComputedStyle(node).getPropertyValue('background-color') ===
          'rgba(0, 0, 0, 0)'
            ? getComputedStyle(node).getPropertyValue('color')
            : getComputedStyle(node).getPropertyValue('background-color'); // Firefox Compatible
        if (
          waveColor === 'rgb(201, 205, 212)' &&
          node.getAttribute('role') === 'switch' &&
          node.getAttribute('class')?.indexOf('mo-switch-type-line') === -1
        ) {
          waveColor = 'rgba(0, 0, 0, 0.85)';
        }

        // console.log('waveColor', waveColor);

        clickWaveTimeoutId = setTimeout(() => onClick(node, waveColor), 0);
        raf.cancel(animationStartId);
        animationStart = true;

        // Render to trigger transition event cost 3 frames. Let's delay 10 frames to reset this.
        animationStartId = raf(() => {
          animationStart = false;
        }, 10);
      };
      node.addEventListener('click', newClick, true);
      // eslint-disable-next-line consistent-return
      return {
        cancel: () => {
          node.removeEventListener('click', newClick, true);
        },
      };
    };
    onMounted(() => {
      nextTick(() => {
        const node = findDOMNode(instance);
        if (node.nodeType !== 1) {
          return;
        }
        eventIns = bindAnimationEvent(node);
      });
    });
    onBeforeUnmount(() => {
      if (eventIns) {
        eventIns.cancel();
      }
      clearTimeout(clickWaveTimeoutId);
      isUnmounted = true;
    });

    return () => {
      return slots.default?.()[0];
    };
  },
});
